import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import { usePageTransition } from '../context/PageTransitionContext.tsx';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './partners.scss';

gsap.registerPlugin(ScrollTrigger);

interface Partner {
  name: string;
  logo: string;
  description: string;
  url?: string;
}

interface Startup {
  name: string;
  description: string;
  image: string;
  url?: string;
}

const partners: Partner[] = [
  {
    name: 'Ru Grafica',
    logo: '/partners/ru_grafica.png',
    description: 'Studio grafico specializzato in branding e comunicazione visiva.',
    url: 'https://www.sararuggeridesigner.it/'
  },
  {
    name: 'Marumars',
    logo: '/partners/marumars.png',
    description: 'Digital communication specialist: SEO, copywriting e social media.',
    url: 'https://marumars.it/'
  },
  {
    name: 'Tenkan Communication',
    logo: '/partners/tenkan_communication.png',
    description: 'Agenzia di comunicazione e marketing strategico.',
    url: 'https://tenkan.it/'
  },
];

const startups: Startup[] = [
  {
    name: 'La Selleria della Bassa',
    description: 'Selleria specializzata in articoli per l\'equitazione.',
    image: '/lavori/siti/home_selleria.png',
    url: 'https://laselleriadellabassa.it/'
  },
];

function Partners() {
  const { navigateWithTransition } = usePageTransition();
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Hero title animation
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Video parallax
    const handleScroll = () => {
      if (videoRef.current) {
        const scrolled = window.pageYOffset;
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cards animation
    const cards = document.querySelectorAll('.partner-card, .startup-card');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Partner e Collaborazioni | Network Professionale - Camilla Cino</title>
        <meta name="description" content="Scopri i miei partner e le collaborazioni professionali. Un network di professionisti per offrirti servizi digitali completi e di qualità." />
        <meta name="keywords" content="partner web design, collaborazioni professionali, network freelance, agenzie partner" />
        <meta property="og:title" content="Partner e Collaborazioni | Network Professionale" />
        <meta property="og:description" content="Scopri i miei partner e le collaborazioni professionali per servizi digitali completi." />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/partners" />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/partners" />
      </Helmet>

      <div className="partners-page">
        {/* Hero Section */}
        <section className="partners-hero">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <div className="hero-label">
                  <span className="label-line"></span>
                  Network
                </div>

                <h1 className="hero-title" ref={heroTitleRef}>
                  <span className="hero-line">I miei</span>
                  <span className="hero-line"><span className="gradient-text">Partner</span></span>
                </h1>

                <p className="hero-description">
                  Collaboro con professionisti e agenzie per offrirti un servizio completo.
                  Insieme, trasformiamo le tue idee in realtà.
                </p>

                <div className="hero-cta-group">
                  <a
                    href="#collaborazioni"
                    className="hero-cta"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('collaborazioni')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Scopri i partner
                    <span className="arrow">↓</span>
                  </a>
                  <a
                    href="#startup"
                    className="hero-cta secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('startup')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Altre attività
                    <span className="arrow">↓</span>
                  </a>
                </div>
              </div>

              <div className="hero-image">
                <img src="/partners/sfera.png" alt="Sfera 3D" />
              </div>
            </div>
          </Container>

          <div className="scroll-indicator">
            <div className="scroll-line"></div>
            <span>Scroll</span>
          </div>
        </section>

        {/* Partners Section */}
        <section className="partners-section" id="collaborazioni">
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">01</span>
                Collaborazioni
              </h2>
            </div>

            <div className="partners-grid">
              {partners.map((partner, index) => (
                <a
                  key={index}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`partner-card ${!partner.url ? 'no-link' : ''}`}
                >
                  <div className="partner-logo">
                    <img src={partner.logo} alt={partner.name} loading="lazy" />
                  </div>
                  <div className="partner-info">
                    <h3 className="partner-name">{partner.name}</h3>
                    <p className="partner-description">{partner.description}</p>
                    {partner.url && (
                      <span className="partner-link">
                        Visita il sito <span className="arrow">→</span>
                      </span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </Container>
        </section>

        {/* Startups Section */}
        <section className="startups-section" id="startup" style={{ backgroundImage: "url('/sfondi/sfondo2.png')" }}>
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">02</span>
                Altre attività
              </h2>
            </div>

            <div className="startups-grid">
              {startups.map((startup, index) => (
                <a
                  key={index}
                  href={startup.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="startup-card"
                >
                  <div className="startup-image">
                    <img src={startup.image} alt={startup.name} loading="lazy" />
                  </div>
                  <div className="startup-info">
                    <h3 className="startup-name">{startup.name}</h3>
                    <p className="startup-description">{startup.description}</p>
                    <span className="startup-link">
                      Scopri di più <span className="arrow">→</span>
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <Container maxWidth="lg">
            <h2 className="cta-title">
              Hai un progetto<br />
              <span className="glitch" data-text="ambizioso?">ambizioso?</span>
            </h2>
            <button
              className="hero-cta"
              onClick={() => navigateWithTransition('/contatti')}
            >
              Parliamone
              <span className="arrow">→</span>
            </button>
          </Container>
        </section>
      </div>
    </>
  );
}

export default Partners;
